import ClientsEditorBase from "./ClientsEditorBase.jsx";
import { TAGS_KEYS } from "../../../other/constants/TagsKeys.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { setClientFieldsDisabledV2 } from "../../../other/utils/otherUtils.jsx";
import { EDIT_MESSAGES, SETTING_TAB_WIDGET_KEYS, VIEW_TYPES } from "../../../other/constants/OtherKeys.jsx";
import { loadUserAvatars } from "../../../other/utils/utils.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import CompanyCamField from "@/bryntum-lib/widget/fields/CompanyCamField.jsx";

export default class ClientsGeneralTab extends ClientsEditorBase {
  static get $name() {
    return "ClientsGeneralTab";
  }

  // Factoryable type name
  static get type() {
    return "clientsgeneraltabv2";
  }

  get swivuDeepLinkSlug() {
    return "general";
  }

  static get configurable() {
    return {
      title: "General",
      tab: {
        icon: "fa-regular fa-memo w-4 h-4"
      },
      contentElementCls: "gap-[1em]",
      layoutStyle: {
        justifyContent: "space-between",
        flexDirection: "column",
        flexWrap: "nowrap"
      },
      autoUpdateFields: true,
      items: {
        fieldsContainer: {
          scrollable: {
            overflowX: "hidden",
            overflowY: "auto"
          },
          contentElementCls: "task-editor-v2-content gap-[0.7em]",
          type: "container",
          defaults: {
            labelWidth: "7em",
            flex: "1 0 100%",
            cls: "client-editor-v2-field"
          },
          items: {
            note: {
              type: "text",
              weight: 90,
              cls: "w-full !flex-[1_0_100%] !@md:flex-[1_0_100%] py-2 border-0 border-l-4 border-solid border-[#e0e0e0] dark:border-[#333] pl-[6px] bg-[#fafafa] dark:bg-[#252525] dark:text-[#a5a5a5]",
              content: "Clients can be customers, members, or any other stakeholder that tasks are performed for."
            },
            clientNameField: {
              type: WIDGET_KEYS.duplicateEntityHelper,
              label: "Client Name",
              weight: 100,
              name: "name",
              storeKey: STORE_KEYS.clientStore,
              placeholder: "Please enter client name first",
              cls: "b-inline b-custom-field-sm first-item",
              // browser try to autocomplete this field with some saved email;
              // this is a hack to avoid browser auto-filing the field,
              // there are many values i tried even "off", but only this one work, (see link for all available values https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values)
              autoComplete: "webauthn"
            },
            clientWebsiteLinkField: {
              type: WIDGET_KEYS.fieldUrl,
              name: "website",
              label: "Website Link",
              weight: 110,
              cls: "b-inline b-custom-field-sm last-item",
              onChange: "up.onRecordChange"
            },
            clientAddressField: {
              type: "text",
              name: "address",
              label: "Address",
              weight: 120,
              cls: "b-inline b-custom-field-sm last-item",
              onChange: "up.onRecordChange"
            },
            companyCamContainer: {
              type: CompanyCamField.type,
              cls: "w-[100%] !flex-initial !flex-[1_0_100%] !@md:flex-[1_0_100%]",
              weight: 125
            },
            clientStatusField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "statusId",
              label: "Status",
              valueField: "id",
              displayField: "tagValue",
              cls: "b-inline b-custom-field-sm last-item",
              weight: 130,
              onChange: "up.onRecordChange",
              clearable: true,
              editMessage: EDIT_MESSAGES.status,
              tabName: SETTING_TAB_WIDGET_KEYS.clientStatusTab,
              onSwivuEditMessage: "onClickSwivuEditMessage"
            },
            clientCategoryField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "categoryId",
              label: "Category",
              valueField: "id",
              displayField: "tagValue",
              cls: "b-inline b-custom-field-sm last-item",
              weight: 140,
              onChange: "up.onRecordChange",
              clearable: true,
              editMessage: EDIT_MESSAGES.category,
              tabName: SETTING_TAB_WIDGET_KEYS.clientCategoryTab,
              onSwivuEditMessage: "onClickSwivuEditMessage"
            },
            clientTagsField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "tagsId",
              label: "Tags",
              multiSelect: true,
              hidePickerOnSelect: true,
              valueField: "id",
              displayField: "tagValue",
              cls: "b-inline b-custom-field-sm last-item",
              weight: 150,
              onChange: "up.onRecordChange",
              editMessage: EDIT_MESSAGES.tag,
              tabName: SETTING_TAB_WIDGET_KEYS.clientTagsTab,
              onSwivuEditMessage: "onClickSwivuEditMessage"
            },
            clientTeamField: {
              type: WIDGET_KEYS.settingsCombo,
              name: "teamId",
              label: "Team",
              valueField: "id",
              displayField: "name",
              cls: "b-inline last-item",
              clearable: true,
              weight: 160,
              editMessage: EDIT_MESSAGES.team,
              tabName: SETTING_TAB_WIDGET_KEYS.teamsTab,
              onSwivuEditMessage: "onClickSwivuEditMessage"
            }
          }
        },
        bbarContainer: {
          type: "container",
          contentElementCls: "shrink-0",
          layoutStyle: {
            justifyContent: "flex-end",
            flexWrap: "nowrap",
            alignItems: "center"
          },
          items: {
            activeUsers: {
              type: "displayfield",
              contentElementCls: "!flex-auto !self-center flex-row-reverse gap-[0.5em] overflow-y-auto swivu-recipients-cell",
              html: null
            },
            saveBtn: {
              text: "Save",
              cls: "text-[#048ded] shrink-0",
              type: "button",
              width: "8em",
              onClick: "up.onClickSaveBtn"
            }
          }
        }
      }
    };
  }

  async loadRecord(record, context, otherParams) {
    if (record.isPhantom) {
      setClientFieldsDisabledV2(this.widgetMap, true);
    }

    const { clientStatusField, clientCategoryField, clientTagsField, clientTeamField, clientNameField, companyCamContainer } = this.widgetMap;

    clientNameField.loadRecord(record, context, {
      editor: otherParams.editor,
      onAfterFieldUpdate: this.onRecordChange,
      recordContainer: this
    });

    clientStatusField.store = context.project.clientTagValues.chain((r) => r.tagSet === TAGS_KEYS.status);
    clientCategoryField.store = context.project.clientTagValues.chain((r) => r.tagSet === TAGS_KEYS.category);
    clientTagsField.store = context.project.clientTagValues.chain((r) => r.tagSet === TAGS_KEYS.tag);
    clientTeamField.store = context.project.teamStore.chain();

    companyCamContainer.loadRecord(record, context, {
      ...otherParams,
      addEntityRecordFromCompanyCamProject: () => {
        this.onRecordChange({ value: this.record.name, source: clientNameField, userAction: true });
      }
    });

    // we need to load record into this tab once all fields get their store first otherwise you'll see integer value in combo field instead of name
    super.loadRecord(record, context, otherParams);

    clientStatusField.editorHandler = clientCategoryField.editorHandler = clientTagsField.editorHandler = clientTeamField.editorHandler = context.editorHandler;
    this.otherParams = otherParams;
  }

  updateEditorTitle(source, value) {
    const { name: key } = source;

    if (key === "name") {
      const swivuClientsEditorV2 = this.up("swivuclientseditorv2");
      if (swivuClientsEditorV2) {
        swivuClientsEditorV2.updateEditorTitle(value, this.record);
      }
    }
  }

  onRecordChange({ value, source, userAction }) {
    // since our client name field is required, setting any other field before client name field would fail the
    // ajax calls, so preventing any update here if clientNameField is not valid
    // Reading `isValid` property will set error on an empty clientName field
    if (!this.record.name) return;

    setClientFieldsDisabledV2(this.widgetMap, false);
    this.updateEditorTitle(source, value);
    if (userAction && this.record.isPhantom && value) {
      this.project.clientsStore.add(this.record);
    }

    if (this.otherParams?.onSwivuTabOnRecordChangeHandler) {
      this.otherParams.onSwivuTabOnRecordChangeHandler({ swivuTab: this, record: this.record, value, source, userAction });
    }
  }

  onClickSwivuEditMessage() {
    this.context.viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings' });
  }

  onActiveUserUpdate(activeUsersData) {
    loadUserAvatars(this.widgetMap.activeUsers, activeUsersData, this.project);
  }
}

ClientsGeneralTab.initClass();
